import React, { Component } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import { NOTE_TYPES } from "@constants";
import { NoteService } from "@services";
import classNames from "classnames";
import { Modal, Loader, Image, Tooltip } from "../../../../../components";

import "./AddNoteModal.scss";

class AddNoteModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			noteTypeId: 1,
			noteComment: "",
			noteImage: null,
			rawNoteImage: null,
			notesLoading: false,
			hasError: false,
			validImageSelected: true,
		};

		this.uploadRef = React.createRef();
	}

	/** Creates a new note for the current job event, uploading and attaching an image if provided */
	addNote = () => {
		const { lang } = global;
		const { jobEventId, locationId, getJobEventNotes } = this.props;
		const { noteImage, rawNoteImage, noteTypeId, noteComment } = this.state;
		const imagePromise = () => {
			if (!noteImage) {
				return Promise.resolve();
			}

			return Promise.resolve(
				NoteService.uploadNoteImage({
					fileData: rawNoteImage,
					queryParams: { locationId },
				}),
			);
		};

		this.setState({ notesLoading: true });

		imagePromise()
			.then(response =>
				NoteService.addNote({
					data: {
						JobEventId: jobEventId,
						LocationId: locationId,
						NoteTypeId: noteTypeId,
						Comment: noteComment,
						CloudFilePictureId: response?.data.Id,
					},
				}),
			)
			.then(() => {
				utils.notify({
					type: "success",
					message:
						noteTypeId === 1
							? lang.t("Note Added successfully")
							: lang.t("Alert Added successfully"),
				});
				this.handleCloseModal();
				getJobEventNotes();
			})
			.catch(() => {
				this.setState({ notesLoading: false });
				utils.notify({
					type: "error",
					message: lang.t("Error adding Note"),
					timeOut: 1500,
				});
			});
	};

	handleOnInputChange = ({ target: { value } }) => this.setState({ noteComment: value });

	handleOnSelectChange = ({ target: { value } }) =>
		this.setState({ noteTypeId: parseInt(value, 10) });

	/**
	 * Handles a user uploading an image along with a note that is being created/edited
	 * @param {Event} e The HTML event handled by this method
	 */
	handleImageUpload = e => {
		const { files } = e.target;
		if (!files || !files[0]) return;
		const [{ type }] = files;
		this.setState({ validImageSelected: type.includes("image") }, () => {
			if (type.includes("image")) {
				const noteImage = URL.createObjectURL(files[0]);
				const rawNoteImage = files[0];
				this.setState({
					noteImage,
					rawNoteImage,
				});
			}
		});
	};

	handleImageUploadOnClick = () => this.uploadRef.click();

	handleImageRemoval = () => this.setState({ noteImage: null, rawNoteImage: null });

	handleCloseModal = () => {
		const { onCancel } = this.props;
		// reset to initial state
		this.setState({
			noteTypeId: 1,
			noteComment: "",
			noteImage: null,
			rawNoteImage: null,
			notesLoading: false,
			hasError: false,
		});
		onCancel();
	};

	handleOnSave = e => {
		e.preventDefault();
		const { noteComment } = this.state;

		if (noteComment.length === 0) {
			this.setState({ hasError: true });
		} else {
			this.setState({ hasError: false });
			this.addNote();
		}
	};

	render() {
		const { lang } = global;
		const { noteTypeId, noteComment, noteImage, notesLoading, hasError, validImageSelected } =
			this.state;
		const { show } = this.props;

		return (
			<Modal
				id="add-note-modal"
				show={show}
				onHide={() => this.handleCloseModal()}
			>
				<Modal.Header closeButton>
					<Modal.Title>Add Note</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group">
						<label htmlFor="note-type">{`${lang.t("Type")}:`}</label>
						<select
							value={noteTypeId}
							name="note-type"
							className="form-control"
							onChange={e => this.handleOnSelectChange(e)}
						>
							{NOTE_TYPES.map(type => (
								<option
									key={`note_type_${type.value}`}
									value={type.value}
								>
									{` ${type.label} `}
								</option>
							))}
						</select>
					</div>
					<div className={classNames("form-group", { "has-error": hasError })}>
						<label htmlFor="note-comment">{`${lang.t("Comments")}:`}</label>
						<textarea
							className="form-control"
							name="note-comment"
							value={noteComment}
							rows="5"
							onChange={e => this.handleOnInputChange(e)}
						/>
					</div>
					<Choose>
						<When condition={noteImage === null}>
							<div className="form-group">
								<label htmlFor="note-image">
									<Choose>
										<When condition={!utils.isDesktopSafari}>
											<button
												type="button"
												id="upload-note-image"
												className="btn btn-primary"
												onClick={() => this.handleImageUploadOnClick()}
											>
												{lang.t("Update Image")}
											</button>
										</When>
										<Otherwise>
											<div className="text-left">
												{lang.t("Update Image")}
											</div>
										</Otherwise>
									</Choose>
									<input
										type="file"
										name="note-image"
										accept=".jpeg,.jpg,.png,.tif,.tiff"
										ref={ref => {
											this.uploadRef = ref;
										}}
										className={!utils.isDesktopSafari ? "hidden" : ""}
										onChange={e => this.handleImageUpload(e)}
									/>
								</label>
							</div>
							<If condition={!validImageSelected}>
								<div className="text-danger">
									{lang.t(
										"Please ensure the selected image has one of the following file extensions:",
									)}
									<br />
									{lang.t("jpeg, jpg, png, tif, tiff")}
								</div>
							</If>
						</When>
						<Otherwise>
							<div className="note-image-container">
								<Image
									src={noteImage}
									fallbackSrc="note image"
									className="img-rounded img-responsive note-image"
								/>
								<Tooltip content={lang.t("Remove Image")}>
									<button
										type="button"
										id="remove-image"
										className="btn btn-link"
										aria-label={lang.t("Remove Image")}
										onClick={() => this.handleImageRemoval()}
									>
										<i className="rkf rkf-cross remove-button" />
									</button>
								</Tooltip>
							</div>
						</Otherwise>
					</Choose>
				</Modal.Body>
				<Modal.Footer>
					<div className="text-right col-sm-12">
						<button
							type="button"
							id="add-note-cancel"
							className="btn btn-default"
							onClick={() => this.handleCloseModal()}
						>
							{lang.t("Cancel")}
						</button>
						<button
							type="button"
							id="add-note-save"
							className="btn btn-primary"
							onClick={e => this.handleOnSave(e)}
						>
							{lang.t("Add Note")}
						</button>
					</div>
				</Modal.Footer>
				<Loader active={notesLoading} />
			</Modal>
		);
	}
}

AddNoteModal.propTypes = {
	show: PropTypes.bool,
	onCancel: PropTypes.func,
	jobEventId: PropTypes.any,
	locationId: PropTypes.any,
	getJobEventNotes: PropTypes.func.isRequired,
};

AddNoteModal.defaultProps = {
	show: false,
	onCancel: () => {},
	jobEventId: undefined,
	locationId: undefined,
};

export default AddNoteModal;
